/**
 * cwear-pdp-main.js
 *
 * Product Detail Page — IIFE JavaScript
 * Handles: Gallery, Thumbnails, Variants, Cart, Countdown, Quantity, Wishlist + Share
 *
 * Architecture:
 *   - Single outer IIFE — no globals, no var leaks
 *   - Each section instance initialized independently via [id^="cwear-pdp-"]
 *   - Three JSON blobs per instance:
 *       #cwear-pdp-config-{sectionId}    — section configuration (labels, option names, etc.)
 *       #cwear-pdp-variants-{sectionId}  — full product.variants array
 *       #cwear-pdp-inventory-{sectionId} — inventory_quantity/policy/management by variant id
 *                                           (product.variants | json omits these fields)
 *   - No ES6+: no arrow functions, no const/let, no template literals, no destructuring
 *
 * Compatibility: IE11+ / all modern browsers
 */

(function () {
  'use strict';

  /* IE11 CustomEvent polyfill.
     BUG FOUND (pre-existing, not introduced here): `var CustomEvent = ...`
     hoists a LOCAL binding named CustomEvent to the top of this whole IIFE
     regardless of the `if` — so in every modern browser (where the if body
     never runs) every `new CustomEvent(...)` call in this file resolved to
     that hoisted-but-never-assigned local variable (undefined), not
     window.CustomEvent, throwing "CustomEvent is not a constructor". This
     silently broke the pre-existing cwear:cart:open and
     cwear:wishlist:updated dispatches too. Renamed the local so nothing in
     this scope shadows the global. */
  if (typeof window.CustomEvent !== 'function') {
    var CustomEventPolyfill = function (event, params) {
      params = params || { bubbles: false, cancelable: false, detail: null };
      var evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
      return evt;
    };
    CustomEventPolyfill.prototype = window.Event ? window.Event.prototype : {};
    window.CustomEvent = CustomEventPolyfill;
  }

  function getEventListenerOptions() {
    var supportsPassive = false;

    try {
      var opts = {};
      Object.defineProperty(opts, 'passive', {
        get: function () {
          supportsPassive = true;
          return false;
        }
      });
      window.addEventListener('testPassive', noop, opts);
      window.removeEventListener('testPassive', noop, opts);
    } catch (e) {
      supportsPassive = false;
    }

    return supportsPassive ? { passive: true } : false;
  }

  function noop() { }

  var passiveListenerOptions = getEventListenerOptions();

  function getDatasetValue(el, key) {
    if (!el) return '';
    if (el.dataset && el.dataset[key] !== undefined) return el.dataset[key];
    return el.getAttribute('data-' + key.replace(/([A-Z])/g, '-$1').toLowerCase()) || '';
  }

  function getObjectKeys(obj) {
    if (Object.keys) return Object.keys(obj);
    var keys = [];
    for (var key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        keys.push(key);
      }
    }
    return keys;
  }

  function scrollElementByX(el, amount) {
    if (!el || typeof amount !== 'number') return;
    el.scrollLeft = el.scrollLeft + amount;
  }

  function scrollElementIntoViewSafe(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      /* inline: 'nearest' — evita centrar a la fuerza, lo que dejaba
         miniaturas vecinas cortadas a la mitad en los bordes (solo
         scrollea lo mínimo necesario para mostrar el thumb completo) */
      el.scrollIntoView({ inline: 'nearest', behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      try {
        el.scrollIntoView(false);
      } catch (err) {
        el.scrollIntoView();
      }
    }
  }

  function updateVariantInUrl(variantId) {
    if (!variantId || !window.history || typeof window.history.replaceState !== 'function') return;

    try {
      if (typeof window.URL === 'function') {
        var url = new window.URL(window.location.href);
        if (url.searchParams && typeof url.searchParams.set === 'function') {
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url.toString());
          return;
        }
      }
    } catch (e) {
      noop();
    }

    try {
      var search = window.location.search || '';
      var hash = window.location.hash || '';
      var base = window.location.pathname || '';
      var nextSearch = search;

      if (typeof window.URLSearchParams === 'function') {
        var params = new window.URLSearchParams(search);
        params.set('variant', variantId);
        nextSearch = '?' + params.toString();
      } else if (search.indexOf('variant=') !== -1) {
        nextSearch = search.replace(/([?&])variant=[^&#]*/i, '$1variant=' + encodeURIComponent(variantId));
      } else if (search) {
        nextSearch = search + '&variant=' + encodeURIComponent(variantId);
      } else {
        nextSearch = '?variant=' + encodeURIComponent(variantId);
      }

      window.history.replaceState({}, '', base + nextSearch + hash);
    } catch (err) {
      noop();
    }
  }

  function getSubmitter(e) {
    if (e && e.submitter) return e.submitter;
    return document.activeElement || null;
  }

  function canUseFetch() {
    return typeof window.fetch === 'function' && typeof window.Promise === 'function';
  }

  function createRejectedPromise(message) {
    if (typeof window.Promise === 'function') {
      return window.Promise.reject(new Error(message));
    }
    return {
      then: function () { return this; },
      catch: function (handler) {
        if (typeof handler === 'function') handler(new Error(message));
        return this;
      }
    };
  }

  /* ─────────────────────────────────────────────────────────────────
   * ENTRY POINT — find all PDP section roots and initialize each one
   * ───────────────────────────────────────────────────────────────── */
  var roots = document.querySelectorAll('[id^="cwear-pdp-"]');

  for (var r = 0; r < roots.length; r++) {
    var rootEl = roots[r];
    /* Only process elements whose ID is exactly cwear-pdp-{sectionId}
       (skip data-injection elements like cwear-pdp-config-* and cwear-pdp-variants-*) */
    /* getAttribute, not .id — a <form> with a child named "id" (Shopify's
       standard hidden variant-id field) clobbers the form's own .id
       property, returning that input instead of the id string. */
    var rootId = rootEl.getAttribute('id');
    if (rootId.indexOf('cwear-pdp-config-') === 0) continue;
    if (rootId.indexOf('cwear-pdp-variants-') === 0) continue;
    if (rootId.indexOf('cwear-pdp-inventory-') === 0) continue;
    if (rootId.indexOf('cwear-pdp-countdown-') === 0) continue;
    if (rootId.indexOf('cwear-pdp-variant-id-') === 0) continue;

    var sid = rootId.replace('cwear-pdp-', '');
    initSection(rootEl, sid);
  }

  /* ─────────────────────────────────────────────────────────────────
   * initSection — boots all 7 modules for a single section instance
   * ───────────────────────────────────────────────────────────────── */
  function initSection(root, sectionId) {
    if (!root) return;

    /* ── Parse config JSON ── */
    var configEl = document.getElementById('cwear-pdp-config-' + sectionId);
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    /* ── Parse variants JSON ── */
    var variantsEl = document.getElementById('cwear-pdp-variants-' + sectionId);
    var variants = [];
    if (variantsEl) {
      try {
        variants = JSON.parse(variantsEl.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    /* ── Merge inventory data (product.variants | json omits it — ver
       comentario en cwear-pdp-main.liquid junto al blob de inventario) ── */
    var inventoryEl = document.getElementById('cwear-pdp-inventory-' + sectionId);
    if (inventoryEl) {
      try {
        var inventoryMap = JSON.parse(inventoryEl.textContent) || {};
        for (var vi = 0; vi < variants.length; vi++) {
          var invData = inventoryMap[variants[vi].id];
          if (invData) {
            variants[vi].inventory_management = invData.inventory_management;
            variants[vi].inventory_policy = invData.inventory_policy;
            variants[vi].inventory_quantity = invData.inventory_quantity;
          }
        }
      } catch (e) {
        /* silencioso — sin datos de inventario, updateStock cae al label genérico */
      }
    }

    /* ── Shared state ── */
    var currentGalleryIndex = 0;
    var currentSelections = {};
    var currentVariant = null;
    var isAddingToCart = false;

    /* ── Cache commonly-used DOM refs ── */
    var heroImg = root.querySelector('.cwear-pdp__hero-img');
    var heroWrap = root.querySelector('.cwear-pdp__hero');
    var thumbTrack = root.querySelector('.cwear-pdp__thumb-track');
    var thumbs = root.querySelectorAll('.cwear-pdp__thumb');
    var segments = root.querySelectorAll('.cwear-pdp__progress-seg');
    var prevBtn = root.querySelector('.cwear-pdp__gallery-prev');
    var nextBtn = root.querySelector('.cwear-pdp__gallery-next');
    var priceEl = root.querySelector('.cwear-pdp__current-price');
    var comparePriceEl = root.querySelector('.cwear-pdp__compare-price');
    var saleBadgeEl = root.querySelector('.cwear-pdp__sale-badge');
    var stockEl = root.querySelector('.cwear-pdp__stock');
    var variantInput = document.getElementById('cwear-pdp-variant-id-' + sectionId);
    var form = root.querySelector('.cwear-pdp__form');
    var addBtn = document.getElementById('cwear-pdp-add-btn-' + sectionId) || root.querySelector('.cwear-pdp__cart-btn');
    var errorEl = root.querySelector('.cwear-pdp__cart-error');
    var qtyInput = root.querySelector('.cwear-pdp__qty-input');
    var qtyMinus = root.querySelector('.cwear-pdp__qty-minus');
    var qtyPlus = root.querySelector('.cwear-pdp__qty-plus');
    var wishlistBtn = root.querySelector('.cwear-pdp__wishlist');
    var shareBtn = root.querySelector('.cwear-pdp__share');

    /* ─────────────────────────────────────────────────────────────
     * MODULE 0 — Klaviyo Tracker
     * Manual Viewed Product + Added to Cart tracking for AJAX PDP
     * ──────────────────────────────────────────────────────────── */
    var klaviyoState = getKlaviyoState();

    function getKlaviyoState() {
      if (!window.__cwearKlaviyoState) {
        window.__cwearKlaviyoState = {
          viewedKeys: {},
          lastAdd: { key: '', ts: 0 }
        };
      }
      return window.__cwearKlaviyoState;
    }

    function isKlaviyoReady() {
      return !!(window._learnq && typeof window._learnq.push === 'function');
    }

    function pushKlaviyoEvent(eventName, payload) {
      if (!isKlaviyoReady()) return false;
      window._learnq.push(['track', eventName, payload]);
      return true;
    }

    function getProductUrl() {
      var url = config.productUrl || window.location.pathname;
      if (/^https?:\/\//.test(url)) return url;
      if (url.charAt(0) !== '/') url = '/' + url;
      return window.location.origin + url;
    }

    function getProductImage(variant, addedItem) {
      if (addedItem && addedItem.image) return addedItem.image;
      if (variant && variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
      if (config.productImage) return config.productImage;
      return '';
    }

    function getPriceCents(variant, addedItem) {
      if (addedItem && typeof addedItem.price === 'number') return addedItem.price;
      if (variant && typeof variant.price === 'number') return variant.price;
      if (typeof config.productPrice === 'number') return config.productPrice;
      return 0;
    }

    function centsToNumber(cents) {
      return Number((cents / 100).toFixed(2));
    }

    function buildViewedProductPayload() {
      var variant = currentVariant || (variants.length ? variants[0] : null);
      var priceCents = getPriceCents(variant, null);
      var productId = config.productId ? String(config.productId) : '';
      var variantId = variant && variant.id ? String(variant.id) : '';
      var productTitle = config.productTitle || document.title || '';

      return {
        Name: productTitle,
        ProductName: productTitle,
        ProductID: productId,
        VariantID: variantId,
        URL: getProductUrl(),
        ImageURL: getProductImage(variant, null),
        Price: centsToNumber(priceCents),
        $value: centsToNumber(priceCents)
      };
    }

    function buildAddedToCartPayload(addedItem, quantity) {
      var variant = currentVariant || (variants.length ? variants[0] : null);

      return {
        Name: config.productTitle,
        ProductID: config.productId,
        VariantID: variant ? variant.id : null,

        Categories: config.productType ? [config.productType] : [],

        ImageURL: getProductImage(variant, addedItem),
        URL: getProductUrl(),

        Brand: config.vendor || '',

        Price: centsToNumber(getPriceCents(variant, addedItem)),
        Quantity: quantity || 1,

        $value: centsToNumber(getPriceCents(variant, addedItem))
      };
    }

    function trackViewedProductOnce() {
      var productId = config.productId ? String(config.productId) : '';
      if (!productId) return;

      var dedupeKey = 'viewed|' + productId + '|' + window.location.pathname;
      if (klaviyoState.viewedKeys[dedupeKey]) return;

      if (pushKlaviyoEvent('Viewed Product', buildViewedProductPayload())) {
        klaviyoState.viewedKeys[dedupeKey] = true;
      }
    }

    function trackAddedToCart(addedItem, quantity) {
      var payload = buildAddedToCartPayload(addedItem, quantity);
      var addKey = 'add|' + payload.ProductID + '|' + payload.VariantID + '|' + String(payload.Quantity);
      var now = Date.now();

      if (klaviyoState.lastAdd.key === addKey && (now - klaviyoState.lastAdd.ts) < 1500) {
        return;
      }

      if (pushKlaviyoEvent('Added to Cart', payload)) {
        klaviyoState.lastAdd.key = addKey;
        klaviyoState.lastAdd.ts = now;
      }
    }

    function initKlaviyoTracking() {
      trackViewedProductOnce();
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 1 — Gallery Controller
     * Manages hero image, progress bar, swipe, thumb active state
     * ──────────────────────────────────────────────────────────── */
    function initGallery() {
      if (!heroImg) return;
      if (thumbs.length === 0) return;

      /* Single-image: no navigation needed */
      if (thumbs.length === 1) {
        goTo(0);
        return;
      }

      /* Touch swipe on hero wrap */
      if (heroWrap) {
        var touchStartX = 0;

        heroWrap.addEventListener('touchstart', function (e) {
          touchStartX = e.touches[0].clientX;
        }, passiveListenerOptions);

        heroWrap.addEventListener('touchend', function (e) {
          var diff = touchStartX - e.changedTouches[0].clientX;
          /* Threshold: 40px — matches cwear-testimonials.liquid line 279 */
          if (Math.abs(diff) < 40) return;
          if (diff > 0) {
            goTo(currentGalleryIndex + 1);
          } else {
            goTo(currentGalleryIndex - 1);
          }
        }, passiveListenerOptions);
      }

      /* Thumb click events */
      for (var i = 0; i < thumbs.length; i++) {
        (function (index) {
          thumbs[index].addEventListener('click', function () {
            goTo(index);
          });
        })(i);
      }

      goTo(0);
    }

    /**
     * goTo — navigate gallery to target index (wraps around)
     * Exposed to VariantManager via return value of initGallery()
     */
    function goTo(index) {
      var total = thumbs.length;
      if (total === 0) return;

      currentGalleryIndex = ((index % total) + total) % total;

      /* Update hero image */
      var activeThumb = thumbs[currentGalleryIndex];
      if (heroImg && activeThumb) {
        heroImg.src = getDatasetValue(activeThumb, 'full') || heroImg.src;
        heroImg.alt = getDatasetValue(activeThumb, 'alt') || '';
      }

      /* Update progress segments */
      for (var s = 0; s < segments.length; s++) {
        if (s === currentGalleryIndex) {
          segments[s].classList.add('is-active');
        } else {
          segments[s].classList.remove('is-active');
        }
      }

      /* Update thumbnail active state */
      for (var t = 0; t < thumbs.length; t++) {
        if (t === currentGalleryIndex) {
          thumbs[t].classList.add('is-active');
        } else {
          thumbs[t].classList.remove('is-active');
        }
      }

      /* Scroll active thumb into view */
      scrollThumbIntoView(currentGalleryIndex);
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 2 — Thumbnail Carousel Arrows
     * Scrollable thumb strip with prev/next arrows + state updates
     * max_visible_thumbs: oculta flechas si hay ≤ N thumbs
     * ──────────────────────────────────────────────────────────── */
    function initThumbnails() {
      if (!thumbTrack) return;

      var maxVisible = config.maxVisibleThumbs || 4;

      /* Si hay igual o menos thumbs que el máximo, ocultar las flechas */
      if (thumbs.length <= maxVisible) {
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
        return; /* No hay nada que scrollear — salir */
      }

      /* Hay más thumbs que el máximo: mostrar flechas y activar scroll */
      if (prevBtn) prevBtn.style.display = '';
      if (nextBtn) nextBtn.style.display = '';

      /* Arrow click handlers — scroll by one thumb + gap */
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          scrollThumbs(-1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          scrollThumbs(1);
        });
      }

      /* Update arrow states on scroll */
      thumbTrack.addEventListener('scroll', updateArrowStates, passiveListenerOptions);

      /* Set initial arrow states */
      updateArrowStates();
    }

    /**
     * scrollThumbs — scroll thumbnail track by direction
     * @param {number} direction  -1 for previous, +1 for next
     */
    function scrollThumbs(direction) {
      if (!thumbTrack) return;
      /* Calcula el paso real: ancho del thumb + gap, leído del DOM
         (el tamaño cambia por breakpoint: 80px mobile, hasta 185px desktop) */
      var firstThumb = thumbs[0];
      var scrollAmount = 88;
      if (firstThumb) {
        var thumbWidth = firstThumb.getBoundingClientRect().width;
        var gap = parseFloat(getComputedStyle(thumbTrack).columnGap || getComputedStyle(thumbTrack).gap) || 0;
        scrollAmount = thumbWidth + gap;
      }
      scrollElementByX(thumbTrack, direction * scrollAmount);
    }

    /**
     * updateArrowStates — toggle opacity / aria-disabled on arrows
     * Threshold: 2px for subpixel rounding safety (matches cwear-product-slider.js)
     */
    function updateArrowStates() {
      if (!thumbTrack) return;
      var atStart = thumbTrack.scrollLeft <= 2;
      var atEnd = thumbTrack.scrollLeft + thumbTrack.clientWidth >= thumbTrack.scrollWidth - 2;

      if (prevBtn) {
        prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        prevBtn.style.opacity = atStart ? '0.3' : '1';
        prevBtn.style.pointerEvents = atStart ? 'none' : 'auto';
      }
      if (nextBtn) {
        nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
        nextBtn.style.opacity = atEnd ? '0.3' : '1';
        nextBtn.style.pointerEvents = atEnd ? 'none' : 'auto';
      }
    }

    /**
     * scrollThumbIntoView — ensure a thumbnail is visible in the track
     * Called by goTo() after changing the active thumb
     */
    function scrollThumbIntoView(index) {
      if (!thumbTrack || !thumbs[index]) return;
      var thumb = thumbs[index];
      scrollElementIntoViewSafe(thumb);
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 3 — Variant Manager
     * Resolves variant from selections, cascades UI updates
     * ──────────────────────────────────────────────────────────── */
    /**
     * isOptionAvailable — partial-match availability check (Dawn-style)
     *
     * Checks whether ANY available variant satisfies:
     *   - exact match on the option being tested
     *   - exact match on every OTHER option that has already been selected
     *
     * Un-selected options are treated as wildcards, so a partial selection
     * (e.g. only Color chosen, Talla not yet chosen) never disables unrelated
     * option groups.
     *
     * @param {string} optionName  — option group under test  (e.g. "Color")
     * @param {string} value       — candidate value          (e.g. "Rojo")
     * @param {Object} [overrideSelections] — optional selections to use instead
     *                                        of currentSelections
     * @returns {boolean}
     */
    function isOptionAvailable(optionName, value, overrideSelections) {
      var optionNames = config.optionNames || [];
      var selections = overrideSelections || currentSelections;

      return variants.some(function (v) {
        if (!v.available) return false;

        for (var i = 0; i < optionNames.length; i++) {
          var name = optionNames[i];

          if (name === optionName) {
            if (v.options[i] !== value) return false;
          } else {
            var selectedValue = selections[name];
            if (selectedValue && v.options[i] !== selectedValue) return false;
          }
        }

        return true;
      });
    }

    /**
     * isOptionGloballyUnavailable — returns true only when NO available variant
     * exists for this value regardless of any other selection.
     * Used to mark options that can never be purchased at all.
     *
     * @param {string} optionName
     * @param {string} value
     * @returns {boolean}
     */
    function isOptionGloballyUnavailable(optionName, value) {
      var optionNames = config.optionNames || [];

      return !variants.some(function (v) {
        if (!v.available) return false;
        var idx = optionNames.indexOf(optionName);
        return idx !== -1 && v.options[idx] === value;
      });
    }

    /**
     * updateOptionsAvailability — classify every option button into one of three
     * states using two CSS classes (no disabled attribute):
     *
     *   (neither class)       — available for the current selection
     *   is-unavailable        — unavailable for the CURRENT combo but still
     *                           clickable; clicking it will cascade-reset the
     *                           conflicting options (Dawn behavior)
     *   is-unavailable-global — no available variant exists for this value at all;
     *                           not clickable under any combination
     */
    function updateOptionsAvailability() {
      var optionNames = config.optionNames || [];
      var allOptionBtns = root.querySelectorAll('.cwear-pdp__size-btn, .cwear-pdp__swatch');

      for (var i = 0; i < allOptionBtns.length; i++) {
        var btn = allOptionBtns[i];

        var optionIndex = parseInt(getDatasetValue(btn, 'optionIndex'), 10);
        var optionName = optionNames[optionIndex];
        var optionValue = getDatasetValue(btn, 'value');

        if (!optionName) continue;

        if (isOptionGloballyUnavailable(optionName, optionValue)) {
          // Truly impossible — no variant carries this value as available
          btn.classList.add('is-unavailable');
          btn.classList.add('is-unavailable-global');
          btn.setAttribute('aria-disabled', 'true');
        } else if (!isOptionAvailable(optionName, optionValue)) {
          // Impossible only for the CURRENT combination — still clickable so
          // the user can switch and the conflicting option resets automatically
          btn.classList.add('is-unavailable');
          btn.classList.remove('is-unavailable-global');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.classList.remove('is-unavailable');
          btn.classList.remove('is-unavailable-global');
          btn.removeAttribute('aria-disabled');
        }

        // Never set the disabled attribute — it swallows click events and
        // prevents the cascade-reset flow in onOptionClick.
        btn.removeAttribute('disabled');
      }
    }


    function initVariants() {
      /* Products with no variants (default only) — skip variant UI */
      if (config.hasOnlyDefaultVariant) {
        /* Still init initial price / stock from the single variant */
        if (variants.length > 0) {
          currentVariant = variants[0];
          updatePrice(currentVariant);
          updateStock(currentVariant);
          updateButton(currentVariant);
          updateQuantityMax(currentVariant);
        }
        return;
      }

      /* Build initial selections from the pre-selected variant (server-rendered state) */
      var optionNames = config.optionNames || [];
      var initialVariantId = config.initialVariantId;
      var initialVariant = null;

      if (initialVariantId) {
        for (var v = 0; v < variants.length; v++) {
          if (variants[v].id === initialVariantId) {
            initialVariant = variants[v];
            break;
          }
        }
      }

      /* Fallback: first available variant */
      if (!initialVariant) {
        for (var a = 0; a < variants.length; a++) {
          if (variants[a].available) {
            initialVariant = variants[a];
            break;
          }
        }
      }

      /* Last fallback: first variant period */
      if (!initialVariant && variants.length > 0) {
        initialVariant = variants[0];
      }

      /* Init currentSelections from the active buttons (HTML server-state is source of truth) */
      var sizeButtons = root.querySelectorAll('.cwear-pdp__size-btn');
      var swatchButtons = root.querySelectorAll('.cwear-pdp__swatch');

      for (var sb = 0; sb < sizeButtons.length; sb++) {
        if (sizeButtons[sb].classList.contains('is-active')) {
          var optIdx = parseInt(getDatasetValue(sizeButtons[sb], 'optionIndex'), 10);
          if (!isNaN(optIdx) && optionNames[optIdx] !== undefined) {
            currentSelections[optionNames[optIdx]] = getDatasetValue(sizeButtons[sb], 'value');
          }
        }
      }
      for (var sw = 0; sw < swatchButtons.length; sw++) {
        if (swatchButtons[sw].classList.contains('is-active')) {
          var swOptIdx = parseInt(getDatasetValue(swatchButtons[sw], 'optionIndex'), 10);
          if (!isNaN(swOptIdx) && optionNames[swOptIdx] !== undefined) {
            currentSelections[optionNames[swOptIdx]] = getDatasetValue(swatchButtons[sw], 'value');
          }
        }
      }

      /* If no selections from buttons, seed from initial variant */
      if (initialVariant && getObjectKeys(currentSelections).length === 0) {
        for (var oi = 0; oi < optionNames.length; oi++) {
          currentSelections[optionNames[oi]] = initialVariant.options[oi];
        }
      }

      /* Bind click on all option buttons (sizes + swatches share the same handler) */
      var allOptionBtns = root.querySelectorAll('.cwear-pdp__size-btn, .cwear-pdp__swatch');
      for (var ob = 0; ob < allOptionBtns.length; ob++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            onOptionClick(btn);
          });
        })(allOptionBtns[ob]);
      }

      /* Init with initial variant */
      if (initialVariant) {
        currentVariant = initialVariant;
        updatePrice(initialVariant);
        updateStock(initialVariant);
        updateButton(initialVariant);
        updateQuantityMax(initialVariant);
        updateImage(initialVariant);
        if (variantInput) variantInput.value = initialVariant.id;
      }
      updateOptionsAvailability();
    }

    /**
     * findBestVariantForSelection — given a partial or full selections object,
     * return the best available variant:
     *   1. Full exact match that is available
     *   2. Full exact match even if unavailable (fallback for display)
     * Returns null if nothing matches at all.
     *
     * @param {Object} selections
     * @returns {Object|null}
     */
    function findBestVariantForSelection(selections) {
      var optionNames = config.optionNames || [];
      var fallback = null;

      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;

        for (var j = 0; j < optionNames.length; j++) {
          if (selections[optionNames[j]] !== undefined &&
            v.options[j] !== selections[optionNames[j]]) {
            match = false;
            break;
          }
        }

        if (match) {
          if (v.available) return v;
          if (!fallback) fallback = v;
        }
      }

      return fallback;
    }

    /**
     * onOptionClick — handle size button or swatch button click (Dawn cascade)
     *
     * Two cases:
     *   A) Button is globally unavailable (is-unavailable-global) → ignore click
     *   B) Button is unavailable only for the current combo (is-unavailable only)
     *      → accept the click, clear ALL other selections, find the best
     *        available variant that has this value, and use its other options
     *        to rebuild currentSelections (cascade-reset, Dawn behavior)
     *   C) Button is fully available → normal flow
     */
    function onOptionClick(btn) {
      // Case A: globally unavailable — no variant carries this value as available
      if (btn.classList.contains('is-unavailable-global')) return;

      var optionIndex = parseInt(getDatasetValue(btn, 'optionIndex'), 10);
      var optionNames = config.optionNames || [];
      var optionName = optionNames[optionIndex];
      if (!optionName) return;

      var value = getDatasetValue(btn, 'value');

      if (btn.classList.contains('is-unavailable')) {
        // Case B: unavailable for current combo — cascade-reset other options.
        // Build a partial selection with ONLY the clicked option, then find the
        // best available variant that satisfies it. Use that variant's options
        // to rebuild ALL of currentSelections (Dawn cascade-reset behavior).
        var partialSelections = {};
        partialSelections[optionName] = value;

        var bestVariant = findBestVariantForSelection(partialSelections);
        if (!bestVariant) return; // No variant at all — bail out

        // Rebuild currentSelections from the resolved variant
        for (var oi = 0; oi < optionNames.length; oi++) {
          currentSelections[optionNames[oi]] = bestVariant.options[oi];
        }

        // Sync is-active buttons for ALL option groups to the new selections
        syncActiveButtonsToSelections();

      } else {
        // Case C: normal click — update only this option's selection, then
        // update the is-active state within this group only.
        currentSelections[optionName] = value;

        var groupBtns = root.querySelectorAll('[data-option-index="' + optionIndex + '"]');
        for (var g = 0; g < groupBtns.length; g++) {
          groupBtns[g].classList.remove('is-active');
          if (groupBtns[g].tagName === 'BUTTON') {
            groupBtns[g].setAttribute('aria-pressed', 'false');
          }
        }
        btn.classList.add('is-active');
        if (btn.tagName === 'BUTTON') btn.setAttribute('aria-pressed', 'true');

        // Scroll into view in case the track is overflowing
        scrollElementIntoViewSafe(btn);

        /* Update option-selected label text (e.g. "Talla: 40")
           Usa data-display-value si existe (talla convertida a MX real);
           si no, cae al value crudo (ej. para colores, que no tienen
           data-display-value). */
        var selectedLabel = root.querySelector(
          '.cwear-pdp__option-selected[data-option-index="' + optionIndex + '"]'
        );
        if (selectedLabel) {
          selectedLabel.textContent = getDatasetValue(btn, 'displayValue') || value;
        }
      }

      // Re-evaluate availability for all buttons after any selection change
      updateOptionsAvailability();

      /* Resolve matching variant and cascade all UI updates */
      var variant = findVariant(currentSelections);
      if (!variant) return;

      currentVariant = variant;
      updatePrice(variant);
      updateStock(variant);
      updateButton(variant);
      updateUrl(variant);
      updateImage(variant);
      updateQuantityMax(variant);
      if (variantInput) variantInput.value = variant.id;
    }

    /**
     * syncActiveButtonsToSelections — set is-active on the button that matches
     * currentSelections[optionName] for every option group, and update the
     * option-selected label text. Used after a cascade-reset in Case B.
     */
    function syncActiveButtonsToSelections() {
      var optionNames = config.optionNames || [];
      var allOptionBtns = root.querySelectorAll('.cwear-pdp__size-btn, .cwear-pdp__swatch');

      for (var i = 0; i < allOptionBtns.length; i++) {
        var b = allOptionBtns[i];
        var idx = parseInt(getDatasetValue(b, 'optionIndex'), 10);
        var name = optionNames[idx];
        if (!name) continue;

        var selectedVal = currentSelections[name];
        var btnVal = getDatasetValue(b, 'value');

        if (btnVal === selectedVal) {
          b.classList.add('is-active');
          if (b.tagName === 'BUTTON') b.setAttribute('aria-pressed', 'true');

          // Also update the label span for this group
          var labelEl = root.querySelector(
            '.cwear-pdp__option-selected[data-option-index="' + idx + '"]'
          );
          if (labelEl) labelEl.textContent = getDatasetValue(b, 'displayValue') || selectedVal;

          // Scroll active button into view in the size carousel track
          scrollElementIntoViewSafe(b);
        } else {
          b.classList.remove('is-active');
          if (b.tagName === 'BUTTON') b.setAttribute('aria-pressed', 'false');
        }
      }
    }

    /**
     * findVariant — search variants array for one matching all currentSelections
     */
    function findVariant(selections) {
      var optionNames = config.optionNames || [];
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < optionNames.length; j++) {
          if (v.options[j] !== selections[optionNames[j]]) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    /**
     * updatePrice — update price + compare-at-price + sale badge
     */
    function updatePrice(variant) {
      if (!variant) return;

      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price, config.moneyFormat);
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price, config.moneyFormat);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      if (saleBadgeEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          saleBadgeEl.textContent = '-' + pct + '%';
          saleBadgeEl.style.display = '';
        } else {
          saleBadgeEl.style.display = 'none';
        }
      }
    }

    /**
     * updateStock — update in-stock / out-of-stock label
     * Shows inventory_quantity if show_stock_count is enabled and inventory is tracked
     */
    function updateStock(variant) {
      if (!stockEl || !variant) return;

      stockEl.classList.remove('cwear-pdp__stock--low', 'cwear-pdp__stock--ok');

      if (variant.available) {
        var label = config.inStockLabel || 'En stock';
        /* Solo mostrar cantidad/alerta si:
           - El setting está activo
           - El producto rastrea inventario (inventory_management != null)
           - La política es "deny" (no vender sin stock)
           - Hay cantidad positiva */
        var qtyKnown =
          config.showStockCount &&
          variant.inventory_management &&
          variant.inventory_policy === 'deny' &&
          variant.inventory_quantity > 0;

        if (qtyKnown) {
          var threshold = parseInt(getDatasetValue(stockEl, 'lowThreshold'), 10) || 10;
          if (variant.inventory_quantity < threshold) {
            var lowTemplate = getDatasetValue(stockEl, 'lowTemplate') || '¡Solo quedan COUNT en stock!';
            label = lowTemplate.replace('COUNT', variant.inventory_quantity);
            stockEl.classList.add('cwear-pdp__stock--low');
          } else {
            label = getDatasetValue(stockEl, 'highLabel') || '¡Disponible en stock!';
            stockEl.classList.add('cwear-pdp__stock--ok');
          }
        } else {
          label = getDatasetValue(stockEl, 'inStockLabel') || label;
        }

        stockEl.textContent = label;
        stockEl.classList.remove('is-out');
        stockEl.classList.add('is-in');
      } else {
        stockEl.textContent = getDatasetValue(stockEl, 'outOfStockLabel') || config.outOfStockLabel || 'Agotado';
        stockEl.classList.remove('is-in');
        stockEl.classList.add('is-out');
      }
    }

    /**
     * updateButton — update Add to Cart button disabled state + label
     */
    function updateButton(variant) {
      if (!addBtn || !variant) return;
      var btnLabel = addBtn.querySelector('.cwear-btn__label');
      if (variant.available) {
        addBtn.disabled = false;
        if (btnLabel) {
          btnLabel.textContent = config.addToCartLabel || 'Agregar al carrito';
        } else {
          addBtn.textContent = config.addToCartLabel || 'Agregar al carrito';
        }
      } else {
        addBtn.disabled = true;
        if (btnLabel) {
          btnLabel.textContent = config.soldOutLabel || 'Agotado';
        } else {
          addBtn.textContent = config.soldOutLabel || 'Agotado';
        }
      }

      /* Avisa a otras secciones de la página (ej. cwear-editorial-hero, que
         no comparte DOM ni estado con esta) cuál es la variante activa y si
         tiene stock — único punto de esta función que se llama en TODOS los
         caminos relevantes (carga inicial, click normal, cascade-reset),
         así que es el lugar correcto para emitirlo una sola vez. */
      document.dispatchEvent(new CustomEvent('cwear:pdp:variant-change', {
        detail: { variant: variant, available: !!variant.available }
      }));

      /* Mismo motivo: único punto que se llama en todos los caminos que
         cambian la variante activa, así que es el lugar correcto para
         (re)arrancar el sondeo de stock para la nueva variante. */
      startStockPolling(variant);
    }

    /**
     * Sondeo de stock bajo — dos clientes pueden estar mirando el mismo
     * producto al mismo tiempo; si uno se lo lleva, el otro se enteraba
     * recién al refrescar la página manualmente. La decisión real de
     * "quién se queda con la última unidad" la resuelve Shopify en el
     * checkout (transacción atómica en su backend) — esto es solo para
     * que la ETIQUETA/botón en pantalla reflejen ese cambio sin que el
     * usuario tenga que hacer nada.
     *
     * Alcance a propósito, para no pesar en productos normales:
     *   - Solo corre mientras el stock ya está bajo (mismo umbral que
     *     "¡Solo quedan COUNT en stock!") -- un producto con stock
     *     normal nunca dispara ni un solo request extra.
     *   - Se pausa solo si la pestaña no está visible (Page Visibility
     *     API) -- no gasta nada en pestañas de fondo.
     *   - Se detiene solo en cuanto se agota (ya no hay nada más que
     *     vigilar) o pasado un tiempo máximo, por si alguien se olvida
     *     la pestaña abierta.
     */
    var STOCK_POLL_INTERVAL_MS = 18000;
    var STOCK_POLL_MAX_DURATION_MS = 30 * 60 * 1000;
    var stockPollTimer = null;
    var stockPollStartedAt = 0;
    var productHandle = config.productHandle || '';

    function isLowStock(variant) {
      if (!variant || !variant.available || !stockEl) return false;
      if (!config.showStockCount) return false;
      if (!variant.inventory_management || variant.inventory_policy !== 'deny') return false;
      if (!(variant.inventory_quantity > 0)) return false;
      var threshold = parseInt(getDatasetValue(stockEl, 'lowThreshold'), 10) || 10;
      return variant.inventory_quantity < threshold;
    }

    function stopStockPolling() {
      if (stockPollTimer) {
        clearTimeout(stockPollTimer);
        stockPollTimer = null;
      }
    }

    function scheduleNextStockPoll() {
      stockPollTimer = setTimeout(pollStock, STOCK_POLL_INTERVAL_MS);
    }

    function pollStock() {
      stockPollTimer = null;

      if (Date.now() - stockPollStartedAt > STOCK_POLL_MAX_DURATION_MS) return;
      if (document.hidden) return; // se reanuda solo via visibilitychange
      if (!productHandle || !currentVariant) return;

      fetch('/products/' + productHandle + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (freshProduct) {
          if (!freshProduct) {
            scheduleNextStockPoll();
            return;
          }

          var freshVariant = null;
          for (var i = 0; i < freshProduct.variants.length; i++) {
            if (freshProduct.variants[i].id === currentVariant.id) {
              freshVariant = freshProduct.variants[i];
              break;
            }
          }
          if (!freshVariant) {
            scheduleNextStockPoll();
            return;
          }

          /* /products/<handle>.js (API pública) NO expone
             inventory_quantity ni inventory_policy -- Shopify no
             comparte el número exacto de stock por este endpoint, solo
             "available" (boolean). Por eso el sondeo solo vigila la
             transición disponible → agotado, no un contador en vivo:
             es exactamente el problema real que hay que resolver
             (que el otro cliente no siga viendo el botón activo cuando
             ya no queda nada), no una simulación de un número que
             Shopify no entrega públicamente. */
          if (freshVariant.available !== currentVariant.available) {
            currentVariant.available = freshVariant.available;
            updateStock(currentVariant);
            /* updateButton también llama a startStockPolling -- si ya se
               agotó, isLowStock() da false y el sondeo se corta solo ahí. */
            updateButton(currentVariant);
          } else if (isLowStock(currentVariant)) {
            scheduleNextStockPoll();
          }
        })
        .catch(function () {
          scheduleNextStockPoll();
        });
    }

    function startStockPolling(variant) {
      stopStockPolling();
      if (!isLowStock(variant)) return;
      stockPollStartedAt = Date.now();
      scheduleNextStockPoll();
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && !stockPollTimer && isLowStock(currentVariant)) {
        scheduleNextStockPoll();
      }
    });

    /**
     * updateUrl — push variant ID into URL query string without reload
     */
    function updateUrl(variant) {
      if (!variant) return;
      updateVariantInUrl(variant.id);
    }

    /**
     * updateImage — switch gallery to the variant's featured_image
     * Searches thumbs by matching src filename fragment
     */
    function updateImage(variant) {
      if (!variant || !variant.featured_image) return;

      var targetSrc = variant.featured_image.src || '';
      /* Extract filename without query params for comparison */
      var targetFile = targetSrc.split('?')[0].split('/').pop();

      for (var t = 0; t < thumbs.length; t++) {
        var thumbFull = (getDatasetValue(thumbs[t], 'full') || '').split('?')[0];
        var thumbFile = thumbFull.split('/').pop();
        if (thumbFile && targetFile && thumbFile === targetFile) {
          goTo(t);
          return;
        }
      }

      /* Fallback: update heroImg directly if thumb not found */
      if (heroImg && variant.featured_image.src) {
        heroImg.src = variant.featured_image.src;
        heroImg.alt = variant.featured_image.alt || '';
      }
    }

    /**
     * updateQuantityMax — update qty input max from variant quantity_rule
     */
    function updateQuantityMax(variant) {
      if (!qtyInput || !variant) return;
      var defaultMax = config.defaultMaxQty || 99;
      var maxQty = (variant.quantity_rule && variant.quantity_rule.max)
        ? variant.quantity_rule.max
        : defaultMax;

      qtyInput.max = maxQty;

      /* Clamp current value to new max */
      var currentVal = parseInt(qtyInput.value, 10) || 1;
      if (currentVal > maxQty) {
        qtyInput.value = maxQty;
      }
    }

    /**
     * formatMoney — convert Shopify cents to formatted money string
     * Supports common Shopify money_format tokens
     */
    function formatMoney(cents, moneyFormat) {
      if (typeof cents !== 'number') return '';

      var amount = (cents / 100).toFixed(2);
      var amountNoDecimals = (cents / 100).toFixed(0);
      var amountWithComma = amount.replace('.', ',');

      var format = moneyFormat || '$ {{amount}}';

      return format
        .replace('{{amount}}', amount)
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace('.', ','));
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 4 — Cart Handler
     * AJAX POST to /cart/add.js + Section Rendering cart bubble sync
     * ──────────────────────────────────────────────────────────── */
    function initCart() {
      if (!form || !addBtn) return;

      form.addEventListener('submit', onAddToCart);
    }

    function onAddToCart(e) {
      var submitter = getSubmitter(e);

      // Si el submit fue disparado por el botón "Comprar ahora" (name="checkout"),
      // dejarlo pasar — Shopify lo maneja nativamente enviando directo al checkout.
      if (submitter && submitter.name === 'checkout') return;

      e.preventDefault();

      /* Guard: prevent double submit */
      if (isAddingToCart) return;

      /* Clear previous errors */
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.removeAttribute('role');
      }

      /* Validate variant selection */
      var variantId = variantInput ? parseInt(variantInput.value, 10) : 0;
      if (!variantId || isNaN(variantId)) {
        showCartError(config.selectVariantError || 'Seleccioná una variante');
        return;
      }

      var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      /* Lock button */
      isAddingToCart = true;
      addBtn.disabled = true;

      var btnLabel = addBtn.querySelector('.cwear-btn__label');
      var originalLabel = btnLabel ? btnLabel.textContent : addBtn.textContent;

      if (btnLabel) {
        btnLabel.textContent = config.addingLabel || 'Agregando...';
      } else {
        addBtn.textContent = config.addingLabel || 'Agregando...';
      }

      /* IE11 requires fetch + Promise polyfills for AJAX cart requests. */
      if (!canUseFetch()) {
        showCartError(config.cartUnavailableError || 'Este navegador necesita un polyfill de fetch para agregar al carrito.');
        addBtn.disabled = false;
        if (btnLabel) {
          btnLabel.textContent = originalLabel;
        } else {
          addBtn.textContent = originalLabel;
        }
        isAddingToCart = false;
        return;
      }

      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (d) {
              throw new Error(d.description || d.message || 'Error al agregar');
            });
          }
          return res.json();
        })
        .then(function (addedItem) {
          trackAddedToCart(addedItem, quantity);

          /* Show success state */
          if (btnLabel) {
            btnLabel.textContent = config.addedLabel || '¡Agregado!';
          } else {
            addBtn.textContent = config.addedLabel || '¡Agregado!';
          }

          /* Update cart bubble via Section Rendering API */
          return updateCartBubble();
        })
        .then(function () {
          /* Abrir el cart modal (cwear-cart-modal.js lo escucha) */
          try {
            document.dispatchEvent(new CustomEvent('cwear:cart:open'));
          } catch (e) {
            noop();
          }

          /* Restore button after 2s */
          setTimeout(function () {
            addBtn.disabled = false;
            if (btnLabel) {
              btnLabel.textContent = config.addToCartLabel || 'Agregar al carrito';
            } else {
              addBtn.textContent = config.addToCartLabel || 'Agregar al carrito';
            }
            isAddingToCart = false;
          }, 2000);
        })
        .catch(function (err) {
          showCartError(err.message || 'Error al agregar al carrito');
          addBtn.disabled = false;
          if (btnLabel) {
            btnLabel.textContent = originalLabel;
          } else {
            addBtn.textContent = originalLabel;
          }
          isAddingToCart = false;
        });
    }

    /**
     * updateCartBubble — refresh Dawn's cart count via Section Rendering API
     * Fails silently if cart-icon-bubble is not in the theme
     */
    function updateCartBubble() {
      if (!canUseFetch()) {
        return createRejectedPromise('fetch unavailable');
      }

      return window.fetch('/?sections=cart-icon-bubble')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          // Dawn bubble
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections && sections['cart-icon-bubble']) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
            var newBubble = doc.querySelector('#cart-icon-bubble');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          }

          // CWEAR header badge — fetch live count from /cart.js
          return window.fetch('/cart.js');
        })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart.item_count;
          document.querySelectorAll('[data-cwear-cart-count]').forEach(function (el) {
            el.textContent = count;
            el.style.display = count > 0 ? 'flex' : 'none';
          });
        })
        .catch(noop);
    }

    /**
     * showCartError — display inline error in the cart error element
     */
    function showCartError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.setAttribute('role', 'alert');
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 5 — Countdown Timer
     * setInterval tick from data-end ISO 8601 attribute
     * ──────────────────────────────────────────────────────────── */
    /**
     * initViewersBadge — "X personas viendo esto ahora mismo"
     * Puramente decorativo: número aleatorio entre 10-100 generado en el
     * navegador, sin backend ni tracking real de tráfico.
     */
    function initViewersBadge() {
      var badgeEl = document.getElementById('cwear-pdp-viewers-' + sectionId);
      if (!badgeEl) return;

      var textEl = badgeEl.querySelector('.cwear-pdp__viewers-text');
      if (!textEl) return;

      var template = getDatasetValue(badgeEl, 'template') || 'COUNT personas están viendo esto ahora mismo';
      var count = Math.floor(Math.random() * (100 - 10 + 1)) + 10;
      textEl.textContent = template.replace('COUNT', count);
    }

    /**
     * initSizeGuideLink — click en el link/alerta de guía de tallas junto
     * al selector de tallas: hace scroll hasta el acordeón "Guía de tallas"
     * y lo abre si estaba cerrado, sin importar el estilo (link normal o
     * alerta de talla de fabricante).
     */
    function initSizeGuideLink() {
      var links = root.querySelectorAll('.cwear-pdp__size-guide-link');
      for (var i = 0; i < links.length; i++) {
        (function (link) {
          link.addEventListener('click', function () {
            var targetId = getDatasetValue(link, 'sizeGuideTarget');
            if (!targetId) return;
            var target = document.getElementById(targetId);
            if (!target) return;
            if (target.tagName === 'DETAILS' && !target.open) {
              target.open = true;
            }
            /* block: 'center' — a diferencia de scrollElementIntoViewSafe
               (pensado para el carrusel de thumbnails con 'nearest'), acá
               el salto es a un acordeón lejano en la página; 'center' da
               la sensación de salto intencional en vez de un scroll mínimo */
            try {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (e) {
              target.scrollIntoView();
            }
          });
        })(links[i]);
      }
    }

    function initCountdown() {
      var countdownEl = document.getElementById('cwear-pdp-countdown-' + sectionId);
      if (!countdownEl) return;

      var endDateStr = getDatasetValue(countdownEl, 'end');
      if (!endDateStr) return;

      var endDate = new Date(endDateStr);
      if (isNaN(endDate.getTime())) {
        /* Invalid date — hide the countdown block */
        countdownEl.style.display = 'none';
        return;
      }

      var timerEl = countdownEl.querySelector('.cwear-pdp__countdown-timer');
      var labelEl = countdownEl.querySelector('.cwear-pdp__countdown-label');
      var expiredLabel = getDatasetValue(countdownEl, 'expiredLabel') || 'Oferta expirada';

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var diff = endDate - new Date();

        if (diff <= 0) {
          clearInterval(timer);
          if (labelEl) labelEl.textContent = expiredLabel;
          if (timerEl) timerEl.style.display = 'none';
          return;
        }

        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var s = Math.floor((diff % 60000) / 1000);

        if (timerEl) {
          timerEl.textContent = pad(d) + ':' + pad(h) + ':' + pad(m) + ':' + pad(s);
        }
      }

      /* Run immediately, then tick every second */
      tick();
      var timer = setInterval(tick, 1000);
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 6 — Quantity Manager
     * +/- buttons with min=1 and max from variant.quantity_rule
     * ──────────────────────────────────────────────────────────── */
    function initQuantity() {
      if (!qtyInput) return;

      if (qtyMinus) {
        qtyMinus.addEventListener('click', decrement);
      }
      if (qtyPlus) {
        qtyPlus.addEventListener('click', increment);
      }
    }

    function increment() {
      var current = parseInt(qtyInput.value, 10) || 1;
      var max = parseInt(qtyInput.max, 10) || (config.defaultMaxQty || 99);
      if (current < max) {
        qtyInput.value = current + 1;
      }
    }

    function decrement() {
      var current = parseInt(qtyInput.value, 10) || 1;
      if (current > 1) {
        qtyInput.value = current - 1;
      }
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 7 — Wishlist + Share
     * localStorage wishlist toggle + clipboard URL copy
     * ──────────────────────────────────────────────────────────── */
    function initWishlist() {
      if (!wishlistBtn) return;

      var productId = String(getDatasetValue(wishlistBtn, 'productId') || '');
      var productHandle = String(getDatasetValue(wishlistBtn, 'productHandle') || '');
      // Formato de storage: "handle|productId" — permite al dashboard hacer el fetch por handle
      // y al mismo tiempo identificar el producto por ID sin depender de un número como URL
      var storageKey = productHandle ? productHandle + '|' + productId : productId;
      if (!productId) return;

      /* Set initial state from localStorage */
      if (isInWishlist(storageKey)) {
        wishlistBtn.classList.add('is-active');
        wishlistBtn.setAttribute('aria-label', config.removeFromWishlistLabel || 'Quitar de favoritos');
      } else {
        wishlistBtn.classList.remove('is-active');
        wishlistBtn.setAttribute('aria-label', config.addToWishlistLabel || 'Agregar a favoritos');
      }

      wishlistBtn.addEventListener('click', function () {
        toggleWishlist(storageKey);
      });
    }

    function isInWishlist(key) {
      try {
        var list = JSON.parse(localStorage.getItem('cwwear_wishlist') || '[]');
        return list.indexOf(key) !== -1;
      } catch (e) {
        return false;
      }
    }

    function toggleWishlist(productId) {
      try {
        var list = JSON.parse(localStorage.getItem('cwwear_wishlist') || '[]');
        var idx = list.indexOf(productId);
        if (idx === -1) {
          list.push(productId);
          wishlistBtn.classList.add('is-active');
          wishlistBtn.setAttribute('aria-label', config.removeFromWishlistLabel || 'Quitar de favoritos');
        } else {
          list.splice(idx, 1);
          wishlistBtn.classList.remove('is-active');
          wishlistBtn.setAttribute('aria-label', config.addToWishlistLabel || 'Agregar a favoritos');
        }
        localStorage.setItem('cwwear_wishlist', JSON.stringify(list));
        // Notificar a otros módulos (header badge, dashboard) que la wishlist cambió
        document.dispatchEvent(new CustomEvent('cwear:wishlist:updated', {
          detail: { count: list.length }
        }));
      } catch (e) {
        noop();
      }
    }

    function initShare() {
      if (!shareBtn) return;

      shareBtn.addEventListener('click', copyUrl);
    }

    function copyUrl() {
      var url = window.location.href;
      var title = document.title || '';

      /* Mobile: Web Share API abre el sheet nativo del OS (WhatsApp, SMS, etc.) */
      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(noop);
        return;
      }

      /* Desktop: fallback — copia al portapapeles y muestra feedback */
      if (!navigator.clipboard) return;

      navigator.clipboard.writeText(url)
        .then(function () {
          shareBtn.classList.add('is-copied');
          var origLabel = shareBtn.getAttribute('aria-label') || '';
          shareBtn.setAttribute('aria-label', config.copiedLabel || 'URL copiada');
          setTimeout(function () {
            shareBtn.classList.remove('is-copied');
            if (origLabel) shareBtn.setAttribute('aria-label', origLabel);
          }, 2000);
        })
        .catch(noop);
    }

    /* ─────────────────────────────────────────────────────────────
     * MODULE 8 — Size Carousel
     * Single-row scrollable size track with prev/next arrow buttons.
     * Arrows appear only when the track overflows (has-overflow class).
     * Auto-scrolls active button into view on init and after selection.
     * ──────────────────────────────────────────────────────────── */
    function initSizeCarousel() {
      var carousels = root.querySelectorAll('.cwear-pdp__sizes-carousel');

      for (var c = 0; c < carousels.length; c++) {
        (function (carousel) {
          var track = carousel.querySelector('.cwear-pdp__sizes-track');
          var prevBtn = carousel.querySelector('.cwear-pdp__sizes-arrow--prev');
          var nextBtn = carousel.querySelector('.cwear-pdp__sizes-arrow--next');

          if (!track) return;

          /* Check overflow and toggle has-overflow to show/hide arrows */
          function updateCarouselState() {
            var overflows = track.scrollWidth > track.clientWidth + 2;
            if (overflows) {
              carousel.classList.add('has-overflow');
            } else {
              carousel.classList.remove('has-overflow');
            }

            if (prevBtn) {
              var atStart = track.scrollLeft <= 2;
              prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
            }
            if (nextBtn) {
              var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
              nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
            }
          }

          /* Scroll track by one "page" (~80% of visible width) */
          function scrollTrack(direction) {
            var amount = Math.round(track.clientWidth * 0.75);
            scrollElementByX(track, direction * amount);
          }

          if (prevBtn) {
            prevBtn.addEventListener('click', function () {
              scrollTrack(-1);
            });
          }
          if (nextBtn) {
            nextBtn.addEventListener('click', function () {
              scrollTrack(1);
            });
          }

          track.addEventListener('scroll', updateCarouselState, passiveListenerOptions);

          /* Scroll active button into view on init */
          var activeBtn = track.querySelector('.cwear-pdp__size-btn.is-active');
          if (activeBtn) scrollElementIntoViewSafe(activeBtn);

          updateCarouselState();

          /* Re-check on resize (viewport width changes) */
          if (typeof window.ResizeObserver === 'function') {
            var ro = new ResizeObserver(function () {
              updateCarouselState();
            });
            ro.observe(track);
          }
        })(carousels[c]);
      }
    }

    /* ─────────────────────────────────────────────────────────────
     * INITIALIZATION — run all modules in dependency order
     * ──────────────────────────────────────────────────────────── */
    initGallery();
    initThumbnails();
    initVariants();
    initSizeCarousel();
    initKlaviyoTracking();
    initCart();
    initViewersBadge();
    initSizeGuideLink();
    initCountdown();
    initQuantity();
    initWishlist();
    initShare();
  }

})();
